// @flow
import * as React from 'react';
import {Intro} from 'sail/Text';
import Group from 'sail/Group';
import {TextInput} from 'sail/Input';
import Spinner from 'sail/Spinner';
import {motion, AnimatePresence} from 'framer-motion';
import ObjectHelpers from './helpers/ObjectHelpers';
import {Success} from './Success';

const {useState} = React;

type PromptProps = {|
  children: Object,
  nextPrompt: Function,
  apiKey: string,
  type: string,
  desiredShape: string,
  sticky: string,
  account: Object,
  promptText: React.Element<any>,
  hint: React.Element<any>,
  success: React.Element<any>,
|};

export const Prompt = ({
  nextPrompt,
  apiKey,
  type,
  desiredShape,
  sticky,
  account,
  promptText,
  hint,
  success,
}: PromptProps) => {
  const [loading, setLoading] = useState(false);
  const [correct, setCorrect] = useState(null);
  const [answer, setAnswer] = useState('');

  const expected =
    type === 'object'
      ? JSON.parse(desiredShape.replace(/'/g, '"'))
      : desiredShape;

  const loadingProps = {};
  let result = null;

  const handleChange = (value) => {
    setCorrect(null);
    setAnswer(value);
  };

  const handleSubmit = async () => {
    setLoading(true);

    // For test purposes
    if (answer === 'correct') {
      setTimeout(() => {
        setLoading(false);
        setCorrect(true);
      }, 1500);
      return;
    } else if (answer === 'incorrect') {
      setTimeout(() => {
        setLoading(false);
        setCorrect(false);
      }, 1500);
      return;
    }

    // Check the shape of the actual against the shape of the expected
    let isCorrect = false;
    switch (type) {
      case 'object':
        const obj = await ObjectHelpers.fetchObject(
          answer.replace(/"/g, ''),
          apiKey,
          account,
        );
        isCorrect = ObjectHelpers.isMatch(obj, expected);
        break;
      case 'short answer':
        isCorrect = true;
        break;
      case 'url':
        isCorrect = answer === expected;
        break;
      default:
        break;
    }
    setLoading(false);
    setCorrect(isCorrect);
  };

  const clickContinue = () => {
    const trimmedAnswer = answer.replace(/"/g, '');
    nextPrompt({sticky, answer: trimmedAnswer});
  };

  const successReactComponent = success || (
    <Success clickContinue={nextPrompt}>Success placeholder</Success>
  );
  const successWithProps = React.cloneElement(successReactComponent, {
    clickContinue,
  });

  if (correct) {
    result = <>{successWithProps}</>;
  } else if (correct === false) {
    result = <>{hint}</>;
  }

  return (
    <AnimatePresence>
      <motion.div
        key="prompt"
        initial={{x: 300, opacity: 0}}
        animate={{x: 0, opacity: 1, transition: {duration: 0.75}}}
        exit={{x: -300, opacity: 0}}
      >
        <Group spacing={8}>
          <Intro>{promptText}</Intro>
          <TextInput
            width="long"
            size="jumbo"
            disabled={loading || correct === true}
            onChange={handleChange}
            value={answer}
            autoFocus
            invalid={correct === false}
            placeholder="Submit your answer"
            end={{
              'aria-label': 'Submit your answer',
              content: loading ? <Spinner /> : null,
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSubmit();
              }
            }}
            {...loadingProps}
          />
          {result}
        </Group>
      </motion.div>
    </AnimatePresence>
  );
};
